import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompanyName } from "@/lib/company";
import { useFlags } from "@/lib/flags";
import { cn } from "@/lib/utils";
import { Page, PageHeader, Card, Avatar, Tag, Loading, plural, shortDate, ago } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Check, Clock, Mail, PenLine, Send } from "lucide-react";

// The mailbox between the founders and each of their people (backend/mailbox_routes.py, owner 2026-10-14): not a chat
// room, letters by subject, each showing when it was sent and when its recipient first opened it. Investors write to the
// founders only, and the founders' list tags their conversations INVESTOR (design_handoff_team_roles/, screen 5).
const SUBJECT_MAX = 150;
const BODY_MAX = 10000;

// who's on the other side: a founder sees the professional; a professional sees the founder who wrote first, or
// "Your founders" when they started it themselves
const otherSide = (t, owner) => (owner ? t.coder?.name : t.started_by?.role === "owner" ? t.started_by.name : "Your founders");

// each recipient's first opening ("Seen by Cody · 14 Oct, 08:15"), or that they haven't opened it yet
function SeenLine({ m, me }) {
  return (
    <div className="flex flex-col gap-0.5 text-sm" data-testid="message-seen">
      <span className="text-muted-foreground">Sent {shortDate(m.sent_at, true)}</span>
      {m.seen.map((s) => {
        const who = s.person.id === me ? "you" : s.person.name;
        return s.at
          ? <span key={s.person.id} className="flex items-center gap-1.5 text-gr-success"><Check className="w-3.5 h-3.5 shrink-0" />Seen by {who} · {shortDate(s.at, true)}</span>
          : <span key={s.person.id} className="flex items-center gap-1.5 text-muted-foreground"><Clock className="w-3.5 h-3.5 shrink-0" />Not seen yet by {who}</span>;
      })}
    </div>
  );
}

const investorOf = (t) => t.coder?.role === "viewer";
const BASES = { owner: "/founder/messages", coder: "/team/messages", viewer: "/investor/messages" };

function Thread({ tid, base, owner, me }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const q = useQuery({ queryKey: ["mailbox", tid], refetchInterval: 15000,
                       queryFn: async () => (await api.get(`/mailbox/${tid}`)).data });
  // opening it marked what was sent to them as seen: the list and the sidebar's count change
  useEffect(() => {
    if (q.data) { qc.invalidateQueries({ queryKey: ["mailbox"], exact: true }); qc.invalidateQueries({ queryKey: ["mailbox-unread"] }); }
  }, [q.dataUpdatedAt]);  // eslint-disable-line react-hooks/exhaustive-deps
  const reply = useMutation({
    mutationFn: async () => (await api.post(`/mailbox/${tid}`, { body: draft.trim() })).data,
    onSuccess: () => { setDraft(""); qc.invalidateQueries({ queryKey: ["mailbox"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const send = () => { if (draft.trim() && !reply.isPending) reply.mutate(); };

  if (q.isError) return <Card className="px-5 py-6 text-muted-foreground">This conversation isn't yours or doesn't exist.</Card>;
  if (!q.data) return <Card className="px-5 py-6"><Loading /></Card>;
  const t = q.data;
  return (
    <div className="flex flex-col gap-3 min-w-0" data-testid="mail-thread">
      <div className="flex items-start gap-2">
        <Button asChild variant="ghost" size="icon" className="lg:hidden shrink-0" aria-label="All messages">
          <Link to={base}><ArrowLeft /></Link></Button>
        <div className="flex flex-col gap-0.5 min-w-0">
          <h2 className="font-display text-[15px] tracking-[0.02em] font-semibold [overflow-wrap:anywhere]">{t.subject}</h2>
          <span className="text-sm text-muted-foreground">
            With {owner ? `${t.coder?.name}${investorOf(t) ? " · Investor" : ""}` : "your founders"} · started {shortDate(t.created_at)}
          </span>
        </div>
      </div>
      {t.messages.map((m) => (
        <Card key={m.id} className={cn("px-5 py-4 flex flex-col gap-3", m.mine && "bg-gr-surface-2")} data-testid="mail-message">
          <div className="flex items-center gap-2.5 min-w-0">
            <Avatar name={m.sender.name} size={24} />
            <span className="font-semibold truncate">{m.mine ? "You" : m.sender.name}</span>
            {m.sender.title && <Tag tone="muted" className="shrink-0">{m.sender.title}</Tag>}
          </div>
          <p className="whitespace-pre-wrap [overflow-wrap:anywhere] leading-relaxed">{m.body}</p>
          <SeenLine m={m} me={me} />
        </Card>
      ))}
      {/* a founder finished and removed them: the conversation stays, nobody writes to them any more */}
      {t.coder?.removed ? (
        <p className="text-sm text-muted-foreground" data-testid="mail-removed">
          {t.coder.name} {investorOf(t) ? "no longer has investor access" : "was removed from the team"}, so they can't read new messages. Invite them again to write to them.
        </p>
      ) : <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <Label htmlFor="mail-reply" className="sr-only">Reply</Label>
        <Textarea id="mail-reply" rows={4} maxLength={BODY_MAX} placeholder="Write a reply" value={draft} data-testid="mail-reply"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }} />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground">
            {investorOf(t) && (owner ? `Only founders and ${t.coder.name.split(" ")[0]} see this conversation. ` : "Only your founders see this conversation. ")}
            Ctrl+Enter to send. Messages can't be changed after they're sent.
          </span>
          <Button type="submit" aria-disabled={!draft.trim()} disabled={reply.isPending} data-testid="mail-reply-send"><Send />Send reply</Button>
        </div>
      </form>}
    </div>
  );
}

// `to`: who it's for, already picked (the assistant's "Message Lena")
function NewMessage({ open, onOpenChange, owner, investor, investors, people, onSent, to }) {
  const [f, setF] = useState({ coder_id: "", subject: "", body: "" });
  const subjectRef = useRef(null);
  useEffect(() => { if (open) setF({ coder_id: to || (people.length === 1 && owner ? people[0].id : ""), subject: "", body: "" }); }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps
  const start = useMutation({
    mutationFn: async () => (await api.post("/mailbox", { ...f, subject: f.subject.trim(), body: f.body.trim(), coder_id: owner ? f.coder_id : null })).data,
    onSuccess: (d) => { onOpenChange(false); onSent(d.id); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const ready = (!owner || f.coder_id) && f.subject.trim() && f.body.trim();
  const missing = owner && !f.coder_id ? "Pick who it's for." : !f.subject.trim() ? "Add a subject." : !f.body.trim() ? "Write your message." : "";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-[18px]" onOpenAutoFocus={(e) => { if (!owner) { e.preventDefault(); subjectRef.current?.focus(); } }}>
        <div className="flex flex-col gap-1">
          <DialogTitle>New message</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {owner ? `To someone on your team${investors ? " or an investor" : ""}. They're told by email, without the message.`
              : "To your founders. They're told by email, without the message."}
          </DialogDescription>
        </div>
        <form className="flex flex-col gap-[18px]" onSubmit={(e) => { e.preventDefault(); if (ready && !start.isPending) start.mutate(); }}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mail-to">To</Label>
            {owner ? (
              <Select value={f.coder_id} onValueChange={(v) => setF({ ...f, coder_id: v })}>
                <SelectTrigger id="mail-to" data-testid="mail-to"><SelectValue placeholder="Pick someone" /></SelectTrigger>
                <SelectContent>
                  {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.title ? ` · ${p.title}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : <span id="mail-to" className="text-gr-ink-secondary">{investor ? `Your founders · ${people.map((p) => p.name).join(", ")}` : people.map((p) => p.name).join(", ") || "Your founders"}</span>}
            {investor && <span className="text-sm text-muted-foreground">As an investor, you write to the founders only.</span>}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mail-subject">Subject</Label>
            <Input id="mail-subject" ref={subjectRef} maxLength={SUBJECT_MAX} placeholder="e.g. Refund policy for the new plans" data-testid="mail-subject"
              value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mail-body">Message</Label>
            <Textarea id="mail-body" rows={7} maxLength={BODY_MAX} data-testid="mail-body"
              value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
            <span className="text-sm text-muted-foreground">You'll see when it's opened. It can't be changed after it's sent.</span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">{missing}</span>
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" aria-disabled={!ready} disabled={start.isPending} data-testid="mail-send"><Send />Send</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Messages() {
  const { tid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const owner = user?.role === "owner";
  const investor = user?.role === "viewer";
  const investors = useFlags().investors;
  const base = BASES[user?.role] || BASES.coder;
  const [composing, setComposing] = useState(false);
  // ?to=<person>: a new message to them opens at once (the assistant's "Message Lena")
  const [params, setParams] = useSearchParams();
  const to = owner ? params.get("to") : null;
  const eyebrow = [useCompanyName(), "Messages"].filter(Boolean).join(" · ");
  const { data, isLoading } = useQuery({ queryKey: ["mailbox"], refetchInterval: 15000, queryFn: async () => (await api.get("/mailbox")).data });

  const people = data?.can_write_to || [];
  const canWrite = people.length > 0;
  const writeTo = people.some((p) => p.id === to) ? to : null;
  useEffect(() => { if (writeTo) setComposing(true); }, [writeTo]);
  const compose = (v) => { setComposing(v); if (!v && to) setParams({}, { replace: true }); };
  const action = (
    <Button onClick={() => setComposing(true)} aria-disabled={!canWrite} disabled={!data || !canWrite} data-testid="mail-new"
      title={canWrite ? undefined : owner ? "Invite someone first." : "Your company has no founder yet."}>
      <PenLine />New message</Button>
  );
  const title = owner ? (investors ? "Messages with your team and investors." : "Messages with your team.") : "Messages with your founders.";
  if (isLoading || !data) return <Page><PageHeader eyebrow={eyebrow} title={title} actions={action} /><Loading /></Page>;

  const threads = data.threads;
  const status = data.unread ? `${plural(data.unread, "new message")} for you.` : "Nothing new for you.";
  return (
    <Page data-testid="messages-page">
      <PageHeader eyebrow={eyebrow} title={title} status={status} actions={action} />
      <div className="grid lg:grid-cols-[320px_minmax(0,1fr)] gap-4 items-start">
        <Card className={cn("p-0 overflow-hidden", tid && "hidden lg:block")} data-testid="mail-list">
          {threads.length ? threads.map((t) => (
            <Link key={t.id} to={`${base}/${t.id}`} data-testid="mail-row"
              className={cn("flex flex-col gap-0.5 px-4 py-3 border-b last:border-b-0 hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                t.id === tid && "bg-gr-hover-strong")}>
              <span className="flex items-center justify-between gap-2 min-w-0">
                <span className={cn("truncate", t.unread ? "font-semibold" : "text-gr-ink-secondary")}>{t.subject}</span>
                {t.unread > 0 && <span className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-foreground text-background text-[11px] leading-5 text-center font-semibold" aria-label={`${t.unread} new`}>{t.unread}</span>}
              </span>
              <span className="flex items-center gap-2 min-w-0 text-sm text-muted-foreground">
                <span className="truncate">{otherSide(t, owner)} · {plural(t.count, "message")} · {ago(t.last_at)}</span>
                {owner && investorOf(t) && <Tag tone="info" className="shrink-0">Investor</Tag>}
              </span>
            </Link>
          )) : (
            <div className="px-4 py-6 flex flex-col items-start gap-1.5 text-muted-foreground" data-testid="mail-empty">
              <Mail className="w-4 h-4" />
              {owner ? "No messages yet. Write to someone on your team: they see when you sent it, and you see when they opened it."
                : "No messages yet. Write to your founders: they see when you sent it, and you see when they opened it."}
            </div>
          )}
        </Card>
        <div className={cn(!tid && "hidden lg:block")}>
          {tid ? <Thread key={tid} tid={tid} base={base} owner={owner} me={user?.id} />
            : <Card className="px-5 py-6 text-muted-foreground">{threads.length ? "Pick a conversation to read it." : "Your conversations will show here."}</Card>}
        </div>
      </div>
      <NewMessage open={composing} onOpenChange={compose} owner={owner} investor={investor} investors={investors} people={people}
        to={writeTo} onSent={(id) => navigate(`${base}/${id}`)} />
    </Page>
  );
}
